'use client';

import type { ChainId } from '@morpho-org/blue-sdk';
import type { ReactTable } from '@tanstack/react-table';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { CompactTable, TableNoFilterResults } from '@morpho-org/ui-v6/client';
import { flexRender } from '@tanstack/react-table';
import type { DashboardLendPositionsFilters } from '@/helpers/filter/table';
import type { UserLendPositionFacets } from '@/hooks/positions/userLendPositionsQuery';
import { InternalLink } from '@/components/common/InternalLink';
import { getMarketUrl } from '@/helpers/routing';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import { trackPortfolioLendPositionClicked } from '@/lib/analytics/client/trackers/portfolio';
import type { LendPositionsTableFeatures } from '../config/LendPositionsTable.columns';
import type { LendPositionsTableRow } from '../config/LendPositionsTable.types';
import DashboardPositionsTable, { DATA_CELL_CONTENT_CLASS, PINNED_CELL_CONTENT_CLASS, POSITION_MANAGER_COLUMN_ID } from '../../DashboardPositionsTable';
import DashboardLendPositionsFiltersComponent from './DashboardLendPositionsFilters';
interface DashboardLendPositionsDesktopViewProps {
  table: ReactTable<LendPositionsTableFeatures, LendPositionsTableRow>;
  filters?: DashboardLendPositionsFilters;
  onFiltersChange?: (filters: DashboardLendPositionsFilters) => void;
  positionChainIds?: ChainId[];
  columnLabels: Record<string, string>;
  totalPositionsCount: number;
  loading: boolean;
  disabled?: boolean;
  facets?: UserLendPositionFacets;
}
const DashboardLendPositionsDesktopView = ({
  table,
  filters,
  onFiltersChange,
  positionChainIds,
  columnLabels,
  totalPositionsCount,
  loading,
  disabled,
  facets
}: DashboardLendPositionsDesktopViewProps) => {
  // 'use no memo': TanStack keeps this object's reference while table state (sorting, rows) changes.
  'use no memo';

  const {
    isSelf
  } = useViewedAccountAddress();
  const dataRows = table.getRowModel().rows;
  const hasFilteredResults = dataRows.length > 0;
  return <div className="border-border bg-card flex min-h-0 flex-col rounded-[20px] border-[0.5px] shadow-md dark:shadow-none">
      <div className="bg-card sticky z-1 overflow-hidden rounded-t-[20px]">
        <div className="p-md box-border content-center rounded-t-[20px]">
          <DashboardLendPositionsFiltersComponent positionChainIds={positionChainIds} filters={filters} onFiltersChange={onFiltersChange} disabled={disabled || loading} facets={facets} />
        </div>
      </div>

      {!hasFilteredResults ? <TableNoFilterResults withBorder withBackground={false} type="markets" count={totalPositionsCount} clearFilters={() => onFiltersChange?.({
      collateralAssetIdsFilter: [],
      loanAssetIdsFilter: [],
      chainIdsFilter: []
    })} /> : <DashboardPositionsTable columns={table.getVisibleLeafColumns()} data-testid="dashboard-lend-positions-table">
          <CompactTable.Head headerGroups={table.getHeaderGroups()} columnLabels={columnLabels} />
          <CompactTable.Body className="[&>tr:last-child_div]:border-b-0">
            {dataRows.map(row => {
          const {
            chainId,
            marketId
          } = row.original.market;
          const marketUrl = getMarketUrl({
            identifier: InterfaceChainUtils.getIdentifierById(chainId),
            marketId,
            collateralSymbol: row.original.market.collateralAsset?.symbol ?? '',
            loanSymbol: row.original.market.loanAsset.symbol,
            hash: isSelf ? 'yourPosition' : undefined
          });
          return <CompactTable.Row key={row.id} data-testid={`lend-market-${marketId}`}>
                  {row.getVisibleCells().map(cell => <CompactTable.Cell key={cell.id}>
                      <InternalLink href={marketUrl} prefetch="on-hover-debounced" onClick={() => trackPortfolioLendPositionClicked({
                chainId,
                marketId
              })}>
                        <CompactTable.CellContent className={cell.column.id === POSITION_MANAGER_COLUMN_ID ? PINNED_CELL_CONTENT_CLASS : DATA_CELL_CONTENT_CLASS}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </CompactTable.CellContent>
                      </InternalLink>
                    </CompactTable.Cell>)}
                </CompactTable.Row>;
        })}
          </CompactTable.Body>
        </DashboardPositionsTable>}
    </div>;
};
export default DashboardLendPositionsDesktopView;